import type { ReactNode } from 'react';
import { IconTriangleExclamation } from '@optiaxiom/icons';
import { Box, Group, LabelMenuButton, Menu, MenuContent, MenuTrigger } from '@optiaxiom/react';
import { useDecision } from '@optimizely/react-sdk';

import { FeatureFlags } from '@frontend/constants/featureFlags';

import type { NotificationStatusFilter, NotificationViewMode } from '../types';
import { CATEGORY_OPTIONS, PRIORITY_OPTIONS, TIME_PRESETS, type TimePreset } from '../utils';

import { AgentFilterMenu } from './AgentFilterMenu';

type NotificationFilterBarProps = {
  status: NotificationStatusFilter;
  /** Hidden on the Action Required tab, which spans read and unread alike. */
  hideStatus?: boolean;
  agentId: string | undefined;
  category: string | undefined;
  /** Hidden on the Action Required tab, where the type set is already fixed. */
  hideCategory?: boolean;
  priority: string | undefined;
  time: TimePreset | undefined;
  // kept for future view-toggle re-introduction
  view: NotificationViewMode;
  onStatusChange: (status: NotificationStatusFilter) => void;
  onAgentChange: (agentId: string | undefined) => void;
  onCategoryChange: (category: string | undefined) => void;
  onPriorityChange: (priority: string | undefined) => void;
  onTimeChange: (time: TimePreset | undefined) => void;
  // kept for future view-toggle re-introduction
  onViewChange: (view: NotificationViewMode) => void;
  actions?: ReactNode;
};

const STATUS_OPTIONS: Array<{ label: string; value: NotificationStatusFilter }> = [
  { label: 'All', value: 'all' },
  { label: 'Read', value: 'read' },
  { label: 'Unread', value: 'unread' },
];

export function NotificationFilterBar({
  status,
  hideStatus = false,
  agentId,
  category,
  hideCategory = false,
  priority,
  time,
  onStatusChange,
  onAgentChange,
  onCategoryChange,
  onPriorityChange,
  onTimeChange,
  actions,
}: NotificationFilterBarProps) {
  const [messagingDecision] = useDecision(FeatureFlags.MESSAGING_ENABLED);
  const isMessagingEnabled = messagingDecision.enabled;

  const visibleCategoryOptions = isMessagingEnabled
    ? CATEGORY_OPTIONS
    : CATEGORY_OPTIONS.filter(opt => opt.value !== 'messaging');

  const statusMenuOptions = STATUS_OPTIONS.map(opt => ({
    label: opt.label,
    selected: status === opt.value,
    execute: () => onStatusChange(opt.value),
  }));

  const categoryMenuOptions = [
    {
      label: 'All',
      selected: category === undefined,
      execute: () => onCategoryChange(undefined),
    },
    ...visibleCategoryOptions.map(opt => ({
      label: opt.label,
      selected: category === opt.value,
      execute: () => onCategoryChange(opt.value),
    })),
  ];

  const priorityMenuOptions = [
    {
      label: 'All',
      selected: priority === undefined,
      execute: () => onPriorityChange(undefined),
    },
    ...PRIORITY_OPTIONS.map(opt => ({
      label: opt.label,
      selected: priority === opt.value,
      execute: () => onPriorityChange(opt.value),
      ...(opt.value === 'critical' && {
        label: '',
        keywords: opt.label,
        addon: (
          <Box alignItems="center" color="fg.error" display="inline-flex" gap="8">
            {opt.label}
            <IconTriangleExclamation />
          </Box>
        ),
      }),
    })),
  ];

  const timeMenuOptions = [
    {
      label: 'All Time',
      selected: time === undefined,
      execute: () => onTimeChange(undefined),
    },
    ...TIME_PRESETS.map(opt => ({
      label: opt.label,
      selected: time === opt.value,
      execute: () => onTimeChange(opt.value),
    })),
  ];

  const statusLabel = STATUS_OPTIONS.find(o => o.value === status)?.label ?? 'All';
  const categoryLabel = CATEGORY_OPTIONS.find(o => o.value === category)?.label ?? 'All';
  const priorityLabel = PRIORITY_OPTIONS.find(o => o.value === priority)?.label ?? 'All';
  const timeLabel = TIME_PRESETS.find(o => o.value === time)?.label ?? 'All Time';

  return (
    <Group justifyContent="space-between">
      <Group gap="8">
        {!hideCategory && (
          <Menu options={categoryMenuOptions}>
            <MenuTrigger asChild>
              <LabelMenuButton label="Category">{categoryLabel}</LabelMenuButton>
            </MenuTrigger>
            <MenuContent />
          </Menu>
        )}

        {!hideStatus && (
          <Menu options={statusMenuOptions}>
            <MenuTrigger asChild>
              <LabelMenuButton label="Status">{statusLabel}</LabelMenuButton>
            </MenuTrigger>
            <MenuContent />
          </Menu>
        )}

        <AgentFilterMenu agentId={agentId} onChange={onAgentChange} />

        <Menu options={priorityMenuOptions}>
          <MenuTrigger asChild>
            <LabelMenuButton label="Priority">{priorityLabel}</LabelMenuButton>
          </MenuTrigger>
          <MenuContent />
        </Menu>

        <Menu options={timeMenuOptions}>
          <MenuTrigger asChild>
            <LabelMenuButton label="Time">{timeLabel}</LabelMenuButton>
          </MenuTrigger>
          <MenuContent />
        </Menu>
      </Group>
      {actions}
    </Group>
  );
}
